import { BaseSocketService, SocketServiceConfig } from '../index';

// ----------- Type Definitions -----------
export interface MiaChatV2Event {
    conversation_id: string;
    message?: {
        role?: 'user' | 'assistant' | 'system';
        content?: string;
        processing_type?: string; // Backend sends processing_type in message
        timestamp?: string;
        sections?: Array<{
            index: number;
            type: string;
            main?: string;
            sub_text?: string;
        }>;
        text?: {
            key: {
                index: number;
                type: string;
                main?: string;
                sub_text?: string;
            };
            value: string;
        };
    };
    email_content?: {
        subject: string;
        sections: Array<{
            type: string;
            content: string;
            order: number;
        }>;
    };
    suggestions?: Array<{
        id: string;
        text: string;
        type?: string;
    }>;
    next_action?: string;
    is_input_restricted_to_suggestions?: boolean;
    processing_type?: 'processing' | 'generating' | 'completed' | 'error';
    status?: 'success' | 'error' | 'processing' | 'completed';
    error?: {
        code: string;
        message: string;
        details?: any;
    };
    // Tool call specific properties (for backward compatibility)
    tool_name?: string;
    tool_parameters?: any;
    tool_result?: any;
    execution_time?: number;
    timestamp: string;
}

export interface MiaChatV2Subscription {
    conversation_id: string;
    callback: (data: MiaChatV2Event) => void;
    active: boolean;
}

export interface SubscribeConversationRequest {
    conversation_id: string;
    token?: string;
}

export interface UnsubscribeConversationRequest {
    conversation_id: string;
}

// ----------- MiaChatV2 Socket Service -----------
class MiaChatV2SocketService extends BaseSocketService {
    private static instance: MiaChatV2SocketService;
    private subscriptions: Map<string, MiaChatV2Subscription> = new Map();
    private eventsBound: boolean = false; // Guard flag to prevent duplicate event listeners
    private readonly STORAGE_KEY = 'miaChatV2_activeConversationId'; // LocalStorage key

    constructor(config: SocketServiceConfig) {
        super(config);
        // Set up event listeners once during construction
        this.setupMiaChatEventListeners();
    }

    // Singleton pattern
    static getInstance(config?: SocketServiceConfig): MiaChatV2SocketService {
        if (!MiaChatV2SocketService.instance) {
            if (!config) {
                throw new Error('MiaChatV2SocketService requires config for first initialization');
            }
            MiaChatV2SocketService.instance = new MiaChatV2SocketService(config);
        }
        return MiaChatV2SocketService.instance;
    }

    // Service name
    getServiceName(): string {
        return 'MiaChatV2SocketService';
    }

    // Events this service handles (matching MCPContext events)
    getEvents(): string[] {
        return [
            'token_usage',           // Token usage updates
            'agent_message',         // Agent message updates
            'processing_status',     // Processing status updates
            'streaming_section_response',    // Streaming response updates
            'conversation_update',   // General conversation updates
            'email_generation',      // Email generation events
            'suggestion_update',     // Suggestion updates
            'executing_tool_call',   // Tool call execution started
            'tool_call_completed',   // Tool call execution completed
            'subscribe_success',     // Subscription confirmation
            'subscribe_error',       // Subscription error
            'unsubscribe_success',   // Unsubscription confirmation
            'unsubscribe_error',     // Unsubscription error
        ];
    }

    // Subscribe to conversation events
    subscribeToConversation(
        conversationId: string, 
        callback: (data: MiaChatV2Event) => void
    ): boolean {
        if (!conversationId || typeof callback !== 'function') {
            console.error('❌ Invalid conversation ID or callback for subscription');
            return false;
        }

        // Check if already subscribed
        if (this.subscriptions.has(conversationId)) {
            console.log(`📡 Already subscribed to conversation: ${conversationId}`);
            // Update callback in case it changed
            const existingSub = this.subscriptions.get(conversationId);
            if (existingSub) {
                existingSub.callback = callback;
            }
            return true;
        }

        // Add to subscriptions map FIRST (before emit)
        // This ensures we track the subscription even if socket is not connected yet
        this.subscriptions.set(conversationId, {
            conversation_id: conversationId,
            callback,
            active: false // Will be set to true on subscribe_success
        });

        // CRITICAL: Store conversation_id in localStorage for reconnect restore
        try {
            localStorage.setItem(this.STORAGE_KEY, conversationId);
            console.log(`💾 [MiaChatV2] Stored conversation_id in localStorage: ${conversationId}`);
        } catch (error) {
            console.warn('⚠️ [MiaChatV2] Failed to store conversation_id in localStorage:', error);
        }

        console.log(`📡 [MiaChatV2] Added subscription to map: ${conversationId} (total: ${this.subscriptions.size})`);

        // Emit subscribe event to server - just conversation ID as string
        // Base service will queue this if socket is not connected
        this.emit('subscribe', conversationId);

        console.log(`📡 [MiaChatV2] Subscribed to conversation: ${conversationId}${this.isConnected() ? '' : ' (queued - will send when connected)'}`);
        return true;
    }

    // Unsubscribe from conversation events
    unsubscribeFromConversation(conversationId: string): boolean {
        if (!conversationId) {
            console.error('❌ Invalid conversation ID for unsubscription');
            return false;
        }

        const subscription = this.subscriptions.get(conversationId);
        if (!subscription) {
            console.log(`📡 No active subscription found for conversation: ${conversationId}`);
            return false;
        }

        // Mark as inactive
        subscription.active = false;

        // Emit unsubscribe event to server - just conversation ID as string
        this.emit('unsubscribe', conversationId);

        // Remove from subscriptions map
        this.subscriptions.delete(conversationId);

        // CRITICAL: Remove conversation_id from localStorage when unsubscribing
        try {
            const storedId = localStorage.getItem(this.STORAGE_KEY);
            if (storedId === conversationId) {
                localStorage.removeItem(this.STORAGE_KEY);
                console.log(`💾 [MiaChatV2] Removed conversation_id from localStorage: ${conversationId}`);
            }
        } catch (error) {
            console.warn('⚠️ [MiaChatV2] Failed to remove conversation_id from localStorage:', error);
        }

        console.log(`📡 Unsubscribed from conversation: ${conversationId}`);
        return true;
    }

    // Get active subscriptions
    getActiveSubscriptions(): string[] {
        return Array.from(this.subscriptions.keys()).filter(id => {
            const sub = this.subscriptions.get(id);
            return sub?.active === true;
        });
    }

    // Check if subscribed to conversation
    isSubscribedToConversation(conversationId: string): boolean {
        const subscription = this.subscriptions.get(conversationId);
        return subscription?.active === true;
    }

    // Get subscription count
    getSubscriptionCount(): number {
        return this.subscriptions.size;
    }

    // Clear all subscriptions
    clearAllSubscriptions(): void {
        this.subscriptions.forEach((subscription, conversationId) => {
            this.unsubscribeFromConversation(conversationId);
        });
        this.subscriptions.clear();
        console.log('📡 Cleared all conversation subscriptions');
    }

    // Handle incoming events
    private handleIncomingEvent(eventName: string, data: any): void {
        try {
            // Add to event history
            this.addEventToHistory(eventName, data);

            // Process based on event type
            switch (eventName) {
                case 'token_usage':
                    this.handleTokenUsage(data);
                    break;
                case 'agent_message':
                    this.handleAgentMessage(data);
                    break;
                case 'processing_status':
                    this.handleProcessingStatus(data);
                    break;
                case 'streaming_section_response':
                    this.handleStreamingResponse(data);
                    break;
                case 'conversation_update':
                    this.handleConversationUpdate(data);
                    break;
                case 'email_generation':
                    this.handleEmailGeneration(data);
                    break;
                case 'suggestion_update':
                    this.handleSuggestionUpdate(data);
                    break;
                case 'executing_tool_call':
                    this.handleExecutingToolCall(data);
                    break;
                case 'tool_call_completed':
                    this.handleToolCallCompleted(data);
                    break;
                case 'subscribe_success':
                    this.handleSubscribeSuccess(data);
                    break;
                case 'subscribe_error':
                    this.handleSubscribeError(data);
                    break;
                case 'unsubscribe_success':
                    this.handleUnsubscribeSuccess(data);
                    break;
                case 'unsubscribe_error':
                    this.handleUnsubscribeError(data);
                    break;
                default:
                    console.log(`📡 Unhandled event: ${eventName}`, data);
            }
        } catch (error) {
            console.error(`❌ Error handling event '${eventName}':`, error);
        }
    }

    // Event handlers
    private handleTokenUsage(data: any): void {
        console.log('📊 Token usage update:', data);
        // Broadcast to all active subscriptions
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleAgentMessage(data: any): void {
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            message: data.message,
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleProcessingStatus(data: any): void {
        console.log('📊 Processing status update:', data);
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            message: data.message || {},
            processing_type: data.processing_type || 'processing',
            status: data.status || 'processing',
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleStreamingResponse(data: any): void {
        console.log('📡 Streaming section response:', data);
        this.broadcastToSubscriptions(data);
    }

    private handleConversationUpdate(data: any): void {
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            message: data.message,
            suggestions: data.suggestions,
            next_action: data.next_action,
            is_input_restricted_to_suggestions: data.is_input_restricted_to_suggestions,
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleEmailGeneration(data: any): void {
        console.log('📧 Email generation:', data);
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            email_content: data.email_content,
            status: data.status || 'success',
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleSuggestionUpdate(data: any): void {
        console.log('💡 Suggestion update:', data);
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            suggestions: data.suggestions,
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleExecutingToolCall(data: any): void {
        console.log('🔧 Executing tool call:', data);
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            message: {
                role: 'system' as const,
                content: data.message?.content || `Executing tool: ${data.tool_name || 'Unknown tool'}`,
                timestamp: new Date().toISOString()
            },
            processing_type: 'processing',
            status: 'processing',
            tool_name: data.tool_name,
            tool_parameters: data.tool_parameters,
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleToolCallCompleted(data: any): void {
        console.log('✅ Tool call completed:', data);
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            message: {
                role: 'system' as const,
                content: data.message?.content || `Tool completed: ${data.tool_name || 'Unknown tool'}`,
                timestamp: new Date().toISOString()
            },
            processing_type: 'completed',
            status: 'completed',
            tool_name: data.tool_name,
            tool_result: data.tool_result,
            execution_time: data.execution_time,
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleSubscribeSuccess(data: any): void {
        console.log('✅ Subscribe success:', data);
        const conversationId = data.conversation_id || data; // Handle both object and string
        if (conversationId && this.subscriptions.has(conversationId)) {
            const subscription = this.subscriptions.get(conversationId);
            if (subscription) {
                subscription.active = true;
                console.log(`📡 Successfully subscribed to conversation: ${conversationId}`);
            }
        } else {
            console.warn(`⚠️ Subscribe success received for unknown conversation: ${conversationId}`);
        }
    }

    private handleSubscribeError(data: any): void {
        console.error('❌ Subscribe error:', data);
        const conversationId = data.conversation_id || data; // Handle both object and string
        if (conversationId && this.subscriptions.has(conversationId)) {
            const subscription = this.subscriptions.get(conversationId);
            if (subscription) {
                subscription.active = false; // Mark as inactive instead of deleting
                console.log(`📡 Marked subscription as inactive for conversation: ${conversationId}`);
                // Optionally retry subscription after a delay
                setTimeout(() => {
                    if (this.subscriptions.has(conversationId) && this.isConnected()) {
                        console.log(`🔄 Retrying subscription for conversation: ${conversationId}`);
                        this.emit('subscribe', conversationId);
                    }
                }, 2000);
            }
        } else {
            console.warn(`⚠️ Subscribe error received for unknown conversation: ${conversationId}`);
        }
    }

    private handleUnsubscribeSuccess(data: any): void {
        console.log('✅ Unsubscribe success:', data);
    }

    private handleUnsubscribeError(data: any): void {
        console.error('❌ Unsubscribe error:', data);
    }

    // Broadcast event to all active subscriptions
    private broadcastToSubscriptions(eventData: MiaChatV2Event): void {
        const conversationId = eventData.conversation_id;
        
        if (conversationId && conversationId !== 'unknown') {
            // Send to specific conversation subscription
            const subscription = this.subscriptions.get(conversationId);
            if (subscription && subscription.active) {
                try {
                    subscription.callback(eventData);
                } catch (error) {
                    console.error(`❌ Error in subscription callback for ${conversationId}:`, error);
                }
            }
        } else {
            // Broadcast to all active subscriptions if no specific conversation ID
            this.subscriptions.forEach((subscription, id) => {
                if (subscription.active) {
                    try {
                        subscription.callback(eventData);
                    } catch (error) {
                        console.error(`❌ Error in subscription callback for ${id}:`, error);
                    }
                }
            });
        }
    }

    // Set up event listeners once (called in constructor)
    // NOTE: Named setupMiaChatEventListeners to avoid conflict with BaseSocketService.setupEventListeners
    private setupMiaChatEventListeners(): void {
        if (this.eventsBound) {
            return; // Already set up, prevent duplicates
        }

        // Set up event listeners for all MiaChatV2 events
        this.getEvents().forEach(eventName => {
            this.on(eventName, (data) => {
                this.handleIncomingEvent(eventName, data);
            });
        });

        this.eventsBound = true;
    }

    // Override connect - event listeners are already set up in constructor
    connect(): void {
        super.connect();
        // Event listeners are already bound in constructor
        // No need to set them up again here
    }

    // Override disconnect - DON'T clear subscriptions, just mark them as inactive
    // This allows subscriptions to be restored on reconnect
    disconnect(): void {
        console.log(`🔌 [MiaChatV2] disconnect() called - preserving ${this.subscriptions.size} subscription(s)`);
        
        // Mark all subscriptions as inactive but KEEP them in the map
        // This allows onReconnect() to restore them later
        this.subscriptions.forEach((subscription, conversationId) => {
            subscription.active = false;
            console.log(`📡 [MiaChatV2] Marked subscription as inactive: ${conversationId}`);
        });
        
        this.eventsBound = false; // Reset flag so listeners can be re-bound if needed
        super.disconnect();
        
        console.log(`✅ [MiaChatV2] Disconnected - ${this.subscriptions.size} subscription(s) preserved for restore`);
    }

    // Override onReconnect to restore subscriptions after reconnection
    protected onReconnect(): void {
        super.onReconnect(); // Call base implementation (emits queued events)
        
        // CRITICAL: Restore subscription from localStorage after reconnect
        // Socket.IO automatically reconnects, but backend forgets subscriptions
        // We restore the conversation_id from localStorage and re-subscribe
        console.log('🔄 [MiaChatV2] onReconnect() called - restoring from localStorage...');
        
        // Small delay to ensure socket is fully ready
        setTimeout(() => {
            if (!this.isConnected()) {
                console.warn('⚠️ [MiaChatV2] Socket not connected, skipping subscription restore');
                return;
            }
            
            // Get conversation_id from localStorage
            try {
                const storedConversationId = localStorage.getItem(this.STORAGE_KEY);
                
                if (!storedConversationId) {
                    console.log('ℹ️ [MiaChatV2] No conversation_id found in localStorage, nothing to restore');
                    return;
                }
                
                console.log(`💾 [MiaChatV2] Found conversation_id in localStorage: ${storedConversationId}`);
                
                // Check if subscription already exists in map
                const existingSubscription = this.subscriptions.get(storedConversationId);
                
                if (existingSubscription) {
                    // Subscription exists in map, just re-emit subscribe
                    console.log(`📡 [MiaChatV2] Restoring existing subscription for conversation: ${storedConversationId}`);
                    this.emit('subscribe', storedConversationId);
                    console.log(`✅ [MiaChatV2] Re-subscribed to conversation: ${storedConversationId}`);
                } else {
                    // Subscription not in map - callback was lost (page refresh, component unmount, etc.)
                    // BUT: We still emit subscribe to backend so backend knows about the subscription
                    // When application layer calls subscribeToConversation() again with callback, it will work
                    console.log(`📡 [MiaChatV2] Conversation_id found in localStorage but no subscription in map: ${storedConversationId}`);
                    console.log(`📡 [MiaChatV2] Callback was lost, but still subscribing to backend...`);
                    console.log(`📡 [MiaChatV2] Application should call subscribeToConversation() again with callback to receive events`);
                    
                    // IMPORTANT: Still emit subscribe to backend
                    // This ensures backend knows about the subscription
                    // When app layer re-subscribes with callback, events will flow
                    this.emit('subscribe', storedConversationId);
                    console.log(`✅ [MiaChatV2] Re-subscribed to backend for conversation: ${storedConversationId}`);
                    console.log(`ℹ️ [MiaChatV2] Note: Events will be received once subscribeToConversation() is called again with callback`);
                }
            } catch (error) {
                console.error('❌ [MiaChatV2] Error reading from localStorage:', error);
            }
        }, 100); // 100ms delay to ensure socket is ready
    }

    // NOTE: eventCache was removed as it was never populated (dead code)
    // If caching is needed in the future, implement it properly with:
    // 1. Cache population in handleIncomingEvent()
    // 2. Cache size limits
    // 3. Cache expiration logic

    // Manually restore subscription from localStorage (useful for debugging or manual trigger)
    restoreSubscriptionFromStorage(): boolean {
        if (!this.isConnected()) {
            console.warn('⚠️ [MiaChatV2] Cannot restore - socket not connected');
            return false;
        }

        try {
            const storedConversationId = localStorage.getItem(this.STORAGE_KEY);
            
            if (!storedConversationId) {
                console.log('ℹ️ [MiaChatV2] No conversation_id found in localStorage');
                return false;
            }

            console.log(`🔄 [MiaChatV2] Manually restoring subscription for: ${storedConversationId}`);
            
            // Check if subscription exists in map
            const existingSubscription = this.subscriptions.get(storedConversationId);
            
            if (existingSubscription) {
                // Subscription exists, just re-emit
                this.emit('subscribe', storedConversationId);
                console.log(`✅ [MiaChatV2] Re-subscribed to conversation: ${storedConversationId}`);
                return true;
            } else {
                // Subscription not in map, but still emit to backend
                console.log(`📡 [MiaChatV2] Subscription not in map, but emitting subscribe to backend: ${storedConversationId}`);
                this.emit('subscribe', storedConversationId);
                console.log(`✅ [MiaChatV2] Emitted subscribe to backend for: ${storedConversationId}`);
                console.log(`ℹ️ [MiaChatV2] Call subscribeToConversation() with callback to receive events`);
                return true;
            }
        } catch (error) {
            console.error('❌ [MiaChatV2] Error restoring from localStorage:', error);
            return false;
        }
    }

    // Get service status
    getServiceStatus(): {
        connected: boolean;
        subscriptionCount: number;
        activeSubscriptions: string[];
        eventHistoryCount: number;
        storedConversationId: string | null;
    } {
        return {
            connected: this.isConnected(),
            subscriptionCount: this.getSubscriptionCount(),
            activeSubscriptions: this.getActiveSubscriptions(),
            eventHistoryCount: this.getEventHistory().length,
            storedConversationId: localStorage.getItem(this.STORAGE_KEY)
        };
    }
}

// Export singleton instance
export const miaChatV2SocketService = MiaChatV2SocketService.getInstance;

// Export the class for testing
export { MiaChatV2SocketService };

// Types are already exported above as interfaces
